import { useEffect, useRef } from 'react'
import { useFStore, streamF } from '@/stores/useFStore'
import { FChatMessage } from './FChatMessage'
import { FChatInput } from './FChatInput'
import { FThinkingIndicator } from './FThinkingIndicator'
import { FThinkingBlock } from './FThinkingBlock'

export function FChat() {
  const {
    sessionId,
    turns,
    workflowStatus,
    streamingThinking,
    streamingResponse,
    isThinking,
    error,
    foolscapJson,
    entityName,
    entityType,
    cardsEnabled,
    // Per-node state - Gatekeeper
    gatekeeperIsProcessing,
    gatekeeperIsThinking,
    gatekeeperStreamingThinking,
    // Per-node state - Evaluator
    evaluatorIsProcessing,
    evaluatorIsThinking,
    evaluatorStreamingThinking,
    // Per-node state - Editor
    editorIsProcessing,
    editorIsThinking,
    editorStreamingThinking,
    // Per-node state - Responder
    responderIsProcessing,
    responderIsThinking,
    responderStreamingThinking,
    // Actions
    setWorkflowStatus,
    setError,
    addUserTurn,
    startStreaming,
    startThinking,
    updateThinking,
    finishThinking,
    updateStreamingResponse,
    finishStreaming,
    setSurfacedCards,
    updateFoolscapJson,
    updateFoolscapMarkdown,
    updateFoolscapMarkdownAnnotated,
    // Per-node actions - Gatekeeper
    startGatekeeperProcessing,
    startGatekeeperThinking,
    updateGatekeeperThinking,
    finishGatekeeperThinking,
    finishGatekeeperProcessing,
    // Per-node actions - Evaluator
    startEvaluatorProcessing,
    startEvaluatorThinking,
    updateEvaluatorThinking,
    finishEvaluatorThinking,
    finishEvaluatorProcessing,
    // Per-node actions - Editor
    startEditorProcessing,
    startEditorThinking,
    updateEditorThinking,
    finishEditorThinking,
    finishEditorProcessing,
    // Per-node actions - Responder
    startResponderProcessing,
    startResponderThinking,
    updateResponderThinking,
    finishResponderThinking,
    finishResponderProcessing,
    resetNodeState,
  } = useFStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive or content is streaming
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming' || workflowStatus === 'processing'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end'
    })
  }, [
    turns,
    streamingThinking,
    streamingResponse,
    isThinking,
    workflowStatus,
    // Per-node streaming thinking
    gatekeeperStreamingThinking,
    evaluatorStreamingThinking,
    editorStreamingThinking,
    responderStreamingThinking,
    // Per-node isThinking flags (trigger scroll when node starts/stops thinking)
    gatekeeperIsThinking,
    evaluatorIsThinking,
    editorIsThinking,
    responderIsThinking,
    // Per-node isProcessing flags (trigger scroll when node starts/stops processing)
    gatekeeperIsProcessing,
    evaluatorIsProcessing,
    editorIsProcessing,
    responderIsProcessing,
  ])

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error, setError])

  const handleSubmit = async (content: string) => {
    if (!content.trim() || workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming') {
      return
    }

    if (!sessionId) {
      setError('No session ID available')
      return
    }

    addUserTurn(content)
    setWorkflowStatus('submitting')

    try {
      startStreaming()
      resetNodeState()

      let fullResponse = ''

      await streamF(
        {
          content,
          session_id: sessionId,
          current_foolscap_json: foolscapJson,
          entity_name: entityName || undefined,
          entity_type: entityType || undefined,
          cards_enabled: cardsEnabled,
        },
        {
          // Legacy single-node callbacks (backwards compat)
          onThinkingStart: () => startThinking(),
          onThinkingDelta: (delta) => updateThinking(delta),
          onThinkingComplete: () => finishThinking(),

          // Gatekeeper callbacks
          onGatekeeperStart: () => startGatekeeperProcessing(),
          onGatekeeperThinkingStart: () => startGatekeeperThinking(),
          onGatekeeperThinkingDelta: (delta) => updateGatekeeperThinking(delta),
          onGatekeeperThinkingComplete: () => finishGatekeeperThinking(),
          onGatekeeperDone: () => finishGatekeeperProcessing(),

          // Evaluator callbacks
          onEvaluatorStart: () => startEvaluatorProcessing(),
          onEvaluatorThinkingStart: () => startEvaluatorThinking(),
          onEvaluatorThinkingDelta: (delta) => updateEvaluatorThinking(delta),
          onEvaluatorThinkingComplete: () => finishEvaluatorThinking(),
          onEvaluatorDone: () => finishEvaluatorProcessing(),

          // Editor callbacks
          onEditorStart: () => startEditorProcessing(),
          onEditorThinkingStart: () => startEditorThinking(),
          onEditorThinkingDelta: (delta) => updateEditorThinking(delta),
          onEditorThinkingComplete: () => finishEditorThinking(),
          onEditorDone: () => finishEditorProcessing(),

          // Responder callbacks
          onResponderStart: () => startResponderProcessing(),
          onResponderThinkingStart: () => startResponderThinking(),
          onResponderThinkingDelta: (delta) => updateResponderThinking(delta),
          onResponderThinkingComplete: () => finishResponderThinking(),
          onResponderDone: () => finishResponderProcessing(),

          // Response and document updates
          onResponseDelta: (delta) => {
            fullResponse += delta
            updateStreamingResponse(delta)
          },
          onCardsSurfaced: (cards) => setSurfacedCards(cards),
          onDocumentUpdate: (json, markdown, _annotatedMarkdown) => {
            updateFoolscapJson(json)
            updateFoolscapMarkdown(markdown)
            // Note: Don't set annotated here - FFinalDiffNode sends it via edits_computed
          },
          // Edits computed from FFinalDiffNode (Zinsser pattern)
          onEditsComputed: (annotatedDoc) => {
            console.log('[FChat] edits_computed received:', annotatedDoc?.length || 0, 'chars')
            updateFoolscapMarkdownAnnotated(annotatedDoc)
          },
          onDone: (output) => {
            finishStreaming(output)
          },
          onError: (err) => {
            setError(err)
          },
        }
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message')
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-0 md:pr-6 scroll-touch overscroll-contain">
        <div className="max-w-2xl py-4 md:py-6 mx-auto md:mx-0">
          {turns.length === 0 && workflowStatus !== 'submitting' && workflowStatus !== 'streaming' ? (
            <div className="text-center py-12">
              <h2 className="text-lg font-light text-foreground mb-2">
                Foolscap Story Studio
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Build your one-page story structure using the Sovereign Entity Framework.
                Start by telling me about your entity (hero).
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {turns.map((turn, index) => (
                <FChatMessage key={index} turn={turn} />
              ))}

              {/* Submitting indicator */}
              {workflowStatus === 'submitting' && (
                <FThinkingIndicator />
              )}

              {/* Per-node streaming thinking blocks (Zinsser pattern) */}
              {/* Show expanded thinking only while processing, collapse to indicator when done */}
              {(workflowStatus === 'streaming' || workflowStatus === 'processing') && (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    {/* Gatekeeper - expanded while processing, collapsed when done */}
                    {gatekeeperIsProcessing && (
                      <div>
                        {gatekeeperStreamingThinking && (
                          <FThinkingBlock
                            thinking={gatekeeperStreamingThinking}
                            isStreaming={gatekeeperIsThinking}
                            isProcessing={gatekeeperIsProcessing}
                            label="Gatekeeper"
                            color="text-sky-600"
                          />
                        )}
                        {!gatekeeperStreamingThinking && (
                          <div className="text-[10px] font-light uppercase tracking-[0.08em] text-sky-600 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
                            <span>Analyzing</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Gatekeeper done indicator */}
                    {gatekeeperStreamingThinking && !gatekeeperIsProcessing && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Gatekeeper</span>
                      </div>
                    )}

                    {/* Evaluator - expanded while processing, collapsed when done */}
                    {evaluatorIsProcessing && (
                      <div>
                        {evaluatorStreamingThinking && (
                          <FThinkingBlock
                            thinking={evaluatorStreamingThinking}
                            isStreaming={evaluatorIsThinking}
                            isProcessing={evaluatorIsProcessing}
                            label="Evaluator"
                            color="text-amber-600"
                          />
                        )}
                        {!evaluatorStreamingThinking && (
                          <div className="text-[10px] font-light uppercase tracking-[0.08em] text-amber-600 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                            <span>Evaluating</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Evaluator done indicator */}
                    {evaluatorStreamingThinking && !evaluatorIsProcessing && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Evaluator</span>
                      </div>
                    )}

                    {/* Editor - expanded while processing, collapsed when done */}
                    {editorIsProcessing && (
                      <div>
                        {editorStreamingThinking && (
                          <FThinkingBlock
                            thinking={editorStreamingThinking}
                            isStreaming={editorIsThinking}
                            isProcessing={editorIsProcessing}
                            label="Editor"
                            color="text-rose-600"
                          />
                        )}
                        {!editorStreamingThinking && (
                          <div className="text-[10px] font-light uppercase tracking-[0.08em] text-rose-600 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                            <span>Editing</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Editor done indicator */}
                    {editorStreamingThinking && !editorIsProcessing && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Editor</span>
                      </div>
                    )}

                    {/* Responder - expanded while processing, collapsed when done */}
                    {responderIsProcessing && (
                      <div>
                        {responderStreamingThinking && (
                          <FThinkingBlock
                            thinking={responderStreamingThinking}
                            isStreaming={responderIsThinking}
                            isProcessing={responderIsProcessing}
                            label="Responder"
                            color="text-violet-600"
                          />
                        )}
                        {!responderStreamingThinking && (
                          <div className="text-[10px] font-light uppercase tracking-[0.08em] text-violet-600 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                            <span>Composing response</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Responder done indicator */}
                    {responderStreamingThinking && !responderIsProcessing && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Responder</span>
                      </div>
                    )}

                    {/* Fallback: legacy single thinking (backwards compat) */}
                    {streamingThinking && !gatekeeperStreamingThinking && !evaluatorStreamingThinking && !editorStreamingThinking && !responderStreamingThinking && (
                      <FThinkingBlock
                        thinking={streamingThinking}
                        isStreaming={isThinking}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Streaming response */}
              {streamingResponse && (
                <FChatMessage
                  turn={{
                    role: 'assistant',
                    content: streamingResponse,
                    timestamp: new Date().toISOString(),
                  }}
                  isStreaming
                />
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 md:px-0 md:pr-6">
          <div className="max-w-2xl mx-auto md:mx-0 py-2 px-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {error}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 py-3 md:py-4 px-4 md:px-0 md:pr-6 border-t border-border md:border-0">
        <div className="max-w-2xl mx-auto md:mx-0">
          <FChatInput
            onSubmit={handleSubmit}
            disabled={workflowStatus === 'submitting' || workflowStatus === 'streaming'}
            placeholder={
              turns.length === 0
                ? "Tell me about the entity you want to write about..."
                : "Continue the conversation..."
            }
          />
        </div>
      </div>
    </div>
  )
}
